import { t } from "@/i18n-js/instance";
import { SubmitButton } from "@/react/components/SettingsApp/Plan/RegularPlan/SubmitButton";
import { EnterpriseOfferBadge } from "@/react/components/shared/EnterpriseOfferBadge";
import { isUpgrading } from "@/react/helpers/plansHelpers";
import { useEnterpriseOfferEnabled } from "@/react/hooks/useEnterpriseOfferEnabled";
import { planTiers } from "@circle-react/helpers/planHelpers";
import { getBillingInterval } from "@circle-react/helpers/plansHelpers";
import type { Plan } from "@circle-react/types";
import { BadgeV2 } from "@circle-react-uikit/BadgeV2";
import { Typography } from "@circle-react-uikit/Typography";

interface PlanHeadersProps {
  plan: Plan;
}

export const PlanHeaders = ({ plan }: PlanHeadersProps) => {
  const strikeThroughPrice = plan.strike_through_price;
  const { isEnterpriseOfferEnabled } = useEnterpriseOfferEnabled();
  const isEnterpriseTier = plan.tier === planTiers.enterprise;
  const shouldShowEnterpriseOffer =
    isEnterpriseTier && isEnterpriseOfferEnabled;

  return (
    <div>
      <div className="mb-6 flex items-center">
        <Typography.BodyLg color="text-default">
          {plan.display_name}
        </Typography.BodyLg>
        {!shouldShowEnterpriseOffer &&
          plan.fetch_discount &&
          !plan.is_on_offer &&
          isUpgrading(plan.plan_action) && (
            <div className="ml-2">
              <BadgeV2 square inverted label={plan.fetch_discount} />
            </div>
          )}

        {!shouldShowEnterpriseOffer &&
          plan.is_on_offer &&
          plan.discount_percentage && (
            <div className="ml-2">
              <style>
                {`
                .plan-coupon-badge {
                  border-color: yellow;
                }
                .plan-coupon-badge > span {
                  color: black !important;
                }
              `}
              </style>
              <BadgeV2
                square
                label={plan.discount_percentage}
                inverted
                className="plan-coupon-badge bg-yellow-500"
              />
            </div>
          )}

        {shouldShowEnterpriseOffer && (
          <div className="ml-2">
            <EnterpriseOfferBadge />
          </div>
        )}
      </div>
      <div className="flex items-start justify-start">
        <div className="mr-1">
          <Typography.TitleLg>
            {plan.is_on_offer
              ? plan.discounted_base_monthly_amount
              : plan.display_base_monthly_amount}
          </Typography.TitleLg>
        </div>
        <Typography.BodyMd>
          {t("pricing_plans.interval.abbreviation.month")}
        </Typography.BodyMd>
      </div>
      <div>
        {strikeThroughPrice && (
          <div className="mr-2 inline-block line-through">
            <Typography.BodySm color="text-light">
              {strikeThroughPrice}
              {t("pricing_plans.interval.abbreviation.month")}
            </Typography.BodySm>
          </div>
        )}
        <div className="inline-block">
          <Typography.BodySm color="text-light">
            {getBillingInterval(plan)}
          </Typography.BodySm>
        </div>
      </div>
      <div className="mt-6 pr-4">
        <SubmitButton plan={plan} />
      </div>
    </div>
  );
};
